import { Button, Divider, FormCheckbox, FormErrorMessage, FormInput, Text, Tooltip } from '$components'
import { BinIcon, PlusIcon, CopyIcon } from '$components'
import { ElementIntent } from '$components'
import { calculateDuration, CreateWorkingHoursSchema, DAY_OF_WEEK, Input } from './'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Fragment } from 'react/jsx-runtime'

export function CreateWorkingHoursContent() {
  const { getValues } = useFormContext<CreateWorkingHoursSchema>()

  return (
    <div className="flex flex-col gap-stack md:gap-stack-roomy">
      {/* Desktop headers - hidden on mobile */}
      <div className="hidden md:grid grid-cols-[1fr_1fr_auto_1fr_auto_auto] gap-x-4 items-center">
        <Text
          type="s"
          className="text-fg-subtle"
        >
          Day
        </Text>

        <Text
          type="s"
          className="text-fg-subtle"
        >
          Start Time
        </Text>

        <Text
          type="s"
          className="text-fg-subtle col-start-4"
        >
          End Time
        </Text>

        <Text
          type="s"
          className="text-fg-subtle"
        >
          Total
        </Text>
      </div>

      {/* Working hours rows */}
      {getValues(Input.WorkingHours).sort((a, b) => Number(a.day) - Number(b.day)).map((dayData, dayIndex) => (
        <WorkingHoursRow
          key={dayData[Input.Day]}
          day={dayData[Input.Day]}
          dayIndex={dayIndex}
        />
      ))}
    </div>
  )
}

type WorkingHoursRowProps = {
  day: string
  dayIndex: number
}

function WorkingHoursRow({ day, dayIndex }: WorkingHoursRowProps) {
  const { control, getValues, watch, formState: { errors }, setValue } = useFormContext<CreateWorkingHoursSchema>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${Input.WorkingHours}.${dayIndex}.${Input.Times}`,
  })

  const dayName = DAY_OF_WEEK[Number(day) as keyof typeof DAY_OF_WEEK]
  const dayActiveId = `${Input.WorkingHours}.${dayIndex}.${Input.Active}`
  const dayTimesPath = `${Input.WorkingHours}.${dayIndex}.${Input.Times}`

  const isDisabled = !watch(Input.WorkingHours)[dayIndex][Input.Active]
  const maxTimeError = errors?.[Input.WorkingHours]?.[dayIndex]?.[Input.Times]?.message ?? errors?.[Input.WorkingHours]?.[dayIndex]?.[Input.Times]?.root?.message

  const copyFromPreviousDay = () => {
    if (dayIndex === 0) {return} // Can't copy from previous day if this is the first day

    const workingHours = getValues(Input.WorkingHours)
    const previousDay = workingHours[dayIndex - 1]

    if (!previousDay || !previousDay[Input.Active] || previousDay[Input.Times].length === 0) {
      return // No previous day data to copy
    }

    // Set the current day as active
    // @ts-expect-error Overriding never type
    setValue(`${Input.WorkingHours}.${dayIndex}.${Input.Active}`, true)

    // Clear existing times and copy from previous day
    // @ts-expect-error Overriding never type
    setValue(`${Input.WorkingHours}.${dayIndex}.${Input.Times}`, previousDay[Input.Times])
  }

  const canCopyFromPreviousDay = () => {
    if (dayIndex === 0) {return false} // Can't copy from previous day if this is the first day

    const workingHours = getValues(Input.WorkingHours)
    const previousDay = workingHours[dayIndex - 1]

    return previousDay && previousDay[Input.Active] && previousDay[Input.Times].length > 0
  }

  return (
    <Fragment>
      {/* Mobile layout */}
      <div className="md:hidden">
        <div className="space-y-stack-tight">
          {/* Day checkbox and name */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-stack-tight">
              <FormCheckbox
                name={dayActiveId}
                id={dayActiveId}
              />
              <label
                htmlFor={dayActiveId}
                className="text-fg-muted text-body-sm font-medium"
              >
                {dayName}
              </label>
            </div>

            {canCopyFromPreviousDay() && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                icon={CopyIcon}
                onClick={copyFromPreviousDay}
                className="text-caption"
              >
                Copy from {DAY_OF_WEEK[(Number(day) - 1) as keyof typeof DAY_OF_WEEK]}
              </Button>
            )}
          </div>

          {!isDisabled
            ? (
              <div className="space-y-stack-tight">
                {fields.map((field, timeIndex) => (
                  <div
                    key={field.id}
                    className="space-y-stack-tight"
                  >
                    {/* Time inputs with action buttons */}
                    <div className="flex gap-stack-tight items-end">
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-stack-tight items-center flex-1">
                        <div>
                          <Text
                            type="xs"
                            className="text-fg-subtle mb-stack-dense"
                          >Start</Text>
                          <FormInput
                            type="time"
                            name={`${dayTimesPath}.${timeIndex}.${Input.From}`}
                          />
                        </div>

                        <span className="text-body-sm pt-stack-roomy text-fg-subtle">to</span>

                        <div>
                          <Text
                            type="xs"
                            className="text-fg-subtle mb-stack-dense"
                          >End</Text>
                          <FormInput
                            type="time"
                            name={`${dayTimesPath}.${timeIndex}.${Input.To}`}
                          />
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-stack-dense mb-stack-dense">
                        {timeIndex === 0 && (
                          <Button
                            variant="ghost"
                            size="md"
                            type="button"
                            icon={PlusIcon}
                            onClick={() => append({ [Input.From]: '08:00', [Input.To]: '12:00' })}
                          />
                        )}

                        {timeIndex > 0 && (
                          <Button
                            variant="ghost"
                            size="md"
                            type="button"
                            icon={BinIcon}
                            intent={ElementIntent.Danger}
                            onClick={() => remove(timeIndex)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Total display */}
                <div className="flex justify-start pt-stack-dense">
                  <Text
                    type="s"
                    className="text-fg-subtle"
                  >
                    Total: {calculateTotalWorkingHours(getValues(Input.WorkingHours)[dayIndex][Input.Times])}
                  </Text>
                </div>

                {fields.length === 0 && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="md"
                      type="button"
                      icon={PlusIcon}
                      onClick={() => append({ [Input.From]: '08:00', [Input.To]: '12:00' })}
                    />
                  </div>
                )}
              </div>
            )
            : (
              <Text
                type="s"
                className="text-fg-subtle"
              >
                Day off
              </Text>
            )
          }

          {maxTimeError && (
            <FormErrorMessage error={maxTimeError} />
          )}
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:grid grid-cols-[1fr_1fr_auto_1fr_auto_auto] gap-x-4 gap-y-4 items-center">
        <div className="flex items-center justify-between col-start-1">
          <div className="flex items-center gap-stack-tight">
            <FormCheckbox
              name={dayActiveId}
              id={dayActiveId}
            />

            <label
              htmlFor={dayActiveId}
              className="text-fg-muted text-body-sm font-medium"
            >
              {dayName}
            </label>
          </div>

          {canCopyFromPreviousDay() && (
            <Tooltip content={`Copy from ${DAY_OF_WEEK[(Number(day) - 1) as keyof typeof DAY_OF_WEEK]}`}>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                icon={CopyIcon}
                onClick={copyFromPreviousDay}
                className="ml-stack-tight"
              />
            </Tooltip>
          )}
        </div>

        {!isDisabled
          ? (
            <Fragment>
              {fields.map((field, timeIndex) => (
                <Fragment key={field.id}>
                  <div className="col-start-2 flex gap-stack-tight items-center">
                    <FormInput
                      type="time"
                      name={`${dayTimesPath}.${timeIndex}.${Input.From}`}
                    />
                  </div>

                  <span className="text-fg-subtle text-body-sm col-start-3">
                    to
                  </span>

                  <div className="col-start-4 flex gap-stack-tight items-center">
                    <FormInput
                      type="time"
                      name={`${dayTimesPath}.${timeIndex}.${Input.To}`}
                    />
                  </div>

                  {timeIndex > 0 && (
                    <Button
                      variant="ghost"
                      size="md"
                      type="button"
                      icon={BinIcon}
                      intent={ElementIntent.Danger}
                      onClick={() => remove(timeIndex)}
                      className="self-center col-start-6"
                    />
                  )}

                  {timeIndex === 0 && (
                    <Fragment>
                      <Text
                        type="s"
                        className="text-fg-subtle col-start-5"
                      >
                        {calculateTotalWorkingHours(getValues(Input.WorkingHours)[dayIndex][Input.Times])}
                      </Text>

                      <Button
                        variant="ghost"
                        size="md"
                        type="button"
                        icon={PlusIcon}
                        onClick={() => append({ [Input.From]: '08:00', [Input.To]: '12:00' })}
                        className="self-center col-start-6"
                      />
                    </Fragment>
                  )}
                </Fragment>
              ))}

              {fields.length === 0 && (
                <Button
                  variant="ghost"
                  size="md"
                  type="button"
                  icon={PlusIcon}
                  onClick={() => append({ [Input.From]: '08:00', [Input.To]: '12:00' })}
                  className="self-center col-start-6"
                />
              )}

              {maxTimeError && (
                <div className="col-start-2 col-span-full">
                  <FormErrorMessage error={maxTimeError} />
                </div>
              )}
            </Fragment>
          )
          : (
            <div className="h-[42px] flex items-center">
              <Text
                type="s"
                className="text-fg-subtle"
              >
                Day off
              </Text>
            </div>
          )}
      </div>

      {/* Divider between days - only on desktop */}
      {dayIndex < getValues(Input.WorkingHours).length - 1 && (
        <Divider />
      )}
    </Fragment>
  )
}

export function calculateTotalWorkingHours(workingHours: CreateWorkingHoursSchema['working_hours'][number]['times']) {
  const totalMinutes = workingHours.reduce((acc, day) => {
    const diffMinutes = calculateDuration(day.from, day.to)
    return acc + diffMinutes
  }, 0)

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}
